import React, { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { view } from '@yola/ws-sdk';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import { designSystem } from '@yola/ws-ui';
import highlighter from 'src/js/modules/highlighter';
import isCompositeImage from 'src/js/modules/common/helpers/is-composite-image';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import designAutomation from 'src/js/modules/design-automation';
import getBreakpointTypeTraitValue from 'src/js/modules/extensions/common/helpers/get-breakpoint-type-trait-value';
import getBreakpointForCurrentDevice from '../../../../breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_BREAKPOINT } from '../../../../breakpoint-editing/constants/breakpoints';
import clearResizedElementsInlineStyles from '../helpers/clear-resized-elements-inline-styles';
import ResizeHandle from '../../../common/components/resize-handle';
import useHandlePosition from '../../../common/hooks/use-handle-position';
import useCompositeItemContainerRect from '../hooks/use-composite-item-container-rect';
import usePositioningCorrection from '../hooks/use-positioning-correction';
import resizeToolPosition from '../../../common/constants/resize-handle-positions';
import getPositionOperations from '../helpers/get-position-operations';
import mergeResponsiveClassOperations from '../helpers/merge-responsive-class-operations';
import trackWidgetDragInitiated from '../trackers/track-widget-drag-initiated';
import trackWidgetDragCompleted from '../trackers/track-widget-drag-completed';
import useDragAndPositioning from '../hooks/use-drag-and-positioning';
import useElementsToStick from '../hooks/use-elements-to-stick';
import PositioningAlignment from './positioning-alignment';

const { useWaitForAutomation } = designAutomation.hooks;
const { Highlighter } = designSystem;

const ImagePositioningTool = ({
  elementId,
  appearanceStyle,
  scrollPosition,
  onActionStart,
  onActionEnd,
}) => {
  const element = view.accessors.getLiveElement(elementId);
  const handleRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const isCompositeItem = isCompositeImage(element);
  const [shiftPressed, setShiftStatus] = useState(false);
  const elementsToStick = useElementsToStick({ element, scrollPosition });
  const waitForAutomationThen = useWaitForAutomation();
  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;

  // TODO: just for testing purposes, remove it later
  const { axisXAlignment, axisYAlignment, handleCorrectionUpdate } = usePositioningCorrection();

  const [handlePosition, updateHandlePosition] = useHandlePosition({
    element,
    scrollPosition,
    positions: {
      [resizeToolPosition.CENTER_CENTER]: {
        correctionX: 0,
        correctionY: 0,
      },
    },
  });

  const showHighlighter = () => {
    highlighter.operations.show([element], {
      forceUpdate: true,
      labelGap: 'medium',
    });
  };

  const hideHighlighter = () => {
    highlighter.operations.hide([element]);
  };

  const handleStart = () => {
    setIsDragging(true);
    onActionStart();
    hideHighlighter();
    showHighlighter();
    trackWidgetDragInitiated({
      elementId,
      ...(isBreakpointEditingEnabled && {
        breakpointType: getBreakpointTypeTraitValue(getBreakpointForCurrentDevice()),
      }),
    });
  };

  const handleMove = (_, { correction }) => {
    handleCorrectionUpdate(correction);
    updateHandlePosition();
    hideHighlighter();
    showHighlighter();
  };

  const handleEnd = () => {
    const breakpoint = isBreakpointEditingEnabled
      ? getBreakpointForCurrentDevice()
      : DESKTOP_BREAKPOINT;

    setIsDragging(false);
    onActionEnd();
    trackWidgetDragCompleted({
      elementId,
      ...(isBreakpointEditingEnabled && {
        breakpointType: getBreakpointTypeTraitValue(breakpoint),
      }),
    });
    view.operations.bulkViewOperations(
      mergeResponsiveClassOperations(
        getPositionOperations([element], breakpoint, isBreakpointEditingEnabled)
      )
    );

    if (isCompositeItem) {
      waitForAutomationThen(() => clearResizedElementsInlineStyles([element]));
    }
  };

  const handleKey = useCallback((event) => {
    const { shiftKey } = event;

    setShiftStatus(shiftKey);
  }, []);

  useEventListener('keydown', handleKey, document);
  useEventListener('keyup', handleKey, document);
  useEventListener('keydown', handleKey, element.ownerDocument);
  useEventListener('keyup', handleKey, element.ownerDocument);

  const { positioningAreaBounds, positioningAreaClipPath } = useDragAndPositioning({
    handleRef,
    elementId,
    scrollPosition,
    elementsToStick,
    onStart: handleStart,
    onMove: handleMove,
    onEnd: handleEnd,
    shiftPressed,
  });

  // If the element is not a composite item, we simply return a rect object with all values
  // set to 0, without performing any calculations.
  const compositeGroupRect = useCompositeItemContainerRect({ element, scrollPosition });

  useEffect(() => {
    showHighlighter();

    return () => {
      hideHighlighter();
    };
    // we need this hook only on mount and unmount
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  if (!handlePosition[resizeToolPosition.CENTER_CENTER]) {
    return null;
  }

  return (
    <Fragment>
      <Highlighter
        appearance="borderless-gradient"
        color="default"
        elementBounds={{
          width: positioningAreaBounds.width,
          height: positioningAreaBounds.height,
          top: positioningAreaBounds.topWithScrollPosition,
          left: positioningAreaBounds.left,
        }}
        clipPath={positioningAreaClipPath}
        visible={isDragging}
      />

      {isDragging && (
        <PositioningAlignment
          scrollPosition={scrollPosition}
          x={axisXAlignment}
          y={axisYAlignment}
        />
      )}

      {isCompositeImage(element) && isDragging && (
        <Highlighter
          appearance="default"
          color="default"
          elementBounds={{
            width: compositeGroupRect.width,
            height: compositeGroupRect.height,
            top: compositeGroupRect.topWithScrollPosition,
            left: compositeGroupRect.left,
          }}
          visible
        />
      )}
      <ResizeHandle
        ref={handleRef}
        direction="all"
        {...(isCompositeItem && { className: 'ws-resize-handle--in-composite-item' })}
        top={handlePosition[resizeToolPosition.CENTER_CENTER][1]}
        left={handlePosition[resizeToolPosition.CENTER_CENTER][0]}
        style={appearanceStyle}
      />
    </Fragment>
  );
};

ImagePositioningTool.propTypes = {
  elementId: PropTypes.string.isRequired,
  scrollPosition: PropTypes.number.isRequired,
  appearanceStyle: PropTypes.shape().isRequired,
  onActionStart: PropTypes.func.isRequired,
  onActionEnd: PropTypes.func.isRequired,
};

export default ImagePositioningTool;
